import { Link } from "react-router-dom";
import { Phone, Mail, MapPin } from "lucide-react";
import { Logo } from "./Logo";

const Footer = () => {
  return (
    <footer className="bg-primary text-primary-foreground">
      <div className="container mx-auto px-4 py-14">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10">
          {/* Brand */}
          <div className="space-y-4">
            <Link
              to="/"
              className="inline-block text-primary-foreground hover:opacity-90 transition-opacity"
              aria-label="Sask Roofing Company Home"
            >
              <Logo className="h-10 w-auto" aria-hidden="true" />
              <span className="sr-only">Sask Roofing Company Home</span>
            </Link>
            <p className="text-primary-foreground/75 leading-relaxed text-sm">
              Saskatchewan-owned and operated. Built for our climate, with
              honest pricing and local crews you can trust.
            </p>
            <div className="pt-2 space-y-2">
              <a
                href="tel:6397397969"
                className="flex items-center gap-2 text-primary-foreground hover:text-accent transition-colors"
              >
                <Phone className="h-5 w-5" />
                <span className="font-bold">(639) 739-7969</span>
              </a>
              <a
                href="mailto:info@saskroofingcompany.com"
                className="flex items-center gap-2 text-primary-foreground/75 hover:text-accent transition-colors"
              >
                <Mail className="h-5 w-5" />
                <span>info@saskroofingcompany.com</span>
              </a>
              <div className="flex items-start gap-2 text-primary-foreground/75 pt-1">
                <MapPin className="h-5 w-5 shrink-0 mt-0.5" />
                <span>Proudly serving homeowners across Saskatchewan.</span>
              </div>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wide mb-4 text-primary-foreground/60">
              Quick Links
            </h3>
            <ul className="space-y-2.5">
              {[
                { name: "Home", href: "/" },
                { name: "About Us", href: "/about" },
                { name: "Our Process", href: "/process" },
                { name: "Financing", href: "/financing" },
                { name: "Cost Calculator", href: "/calculator" },
                { name: "Commercial", href: "/commercial" },
                { name: "Blog", href: "/blog" },
                { name: "FAQ", href: "/faq" },
                { name: "Contact Us", href: "/contact" },
              ].map((l) => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    className="text-primary-foreground/75 hover:text-accent transition-colors text-sm"
                  >
                    {l.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Services */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wide mb-4 text-primary-foreground/60">
              Our Services
            </h3>
            <ul className="grid grid-cols-1 gap-2.5">
              {[
                { name: "Roof Replacement", href: "/services/replacement" },
                { name: "Roof Repair", href: "/services/repair" },
                { name: "Storm & Hail Damage", href: "/services/storm-damage" },
                { name: "Roof Inspections", href: "/services/inspections" },
                { name: "Asphalt Shingles", href: "/services/asphalt" },
                { name: "Metal Roofing", href: "/services/metal" },
                { name: "Commercial Roofing", href: "/services/commercial" },
                {
                  name: "Eavestrough & Gutters",
                  href: "/services/eavestrough",
                },
              ].map((l) => (
                <li key={l.href}>
                  <Link
                    to={l.href}
                    className="text-primary-foreground/75 hover:text-accent transition-colors text-sm"
                  >
                    {l.name}
                  </Link>
                </li>
              ))}
            </ul>
          </div>

          {/* Service Areas */}
          <div>
            <h3 className="text-sm font-bold uppercase tracking-wide mb-4 text-primary-foreground/60">
              Service Areas
            </h3>
            <ul className="grid grid-cols-2 gap-x-4 gap-y-2.5">
              {[
                { name: "Saskatoon", slug: "saskatoon" },
                { name: "Regina", slug: "regina" },
                { name: "Moose Jaw", slug: "moose-jaw" },
                { name: "Prince Albert", slug: "prince-albert" },
                { name: "Swift Current", slug: "swift-current" },
                { name: "Yorkton", slug: "yorkton" },
                { name: "Estevan", slug: "estevan" },
                { name: "Weyburn", slug: "weyburn" },
              ].map((c) => (
                <li key={c.slug}>
                  <Link
                    to={`/areas/${c.slug}`}
                    className="text-primary-foreground/75 hover:text-accent transition-colors text-sm"
                  >
                    {c.name}
                  </Link>
                </li>
              ))}
            </ul>
            <div className="mt-4 pt-3 border-t border-primary-foreground/15">
              <Link
                to="/areas"
                className="text-accent hover:text-accent/80 font-medium text-sm inline-flex items-center gap-1"
              >
                View all service areas →
              </Link>
            </div>
          </div>
        </div>

        <div className="mt-12 pt-6 border-t border-primary-foreground/15 flex flex-col md:flex-row justify-between items-center gap-4">
          <p className="text-primary-foreground/50 text-sm text-center md:text-left">
            &copy; {new Date().getFullYear()} Sask Roofing Company. All rights
            reserved.
          </p>
          <div className="flex gap-4">
            <span className="text-primary-foreground/50 text-sm">
              Powered by{" "}
              <a
                href="https://www.povdigital.com"
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-accent transition-colors"
              >
                POV 360
              </a>
            </span>
          </div>
        </div>
      </div>
    </footer>
  );
};

export default Footer;
